import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { truncatedNames } from '@frontend/thread-header';
import { theme } from '@frontend/theme';
import { Text, useTooltip, styles as dsStyles } from '@frontend/design-system';
import { tooltipContainerStyles, hiddenNamesTriggerStyles } from './truncated-group-names.styles';

type TruncatedGroupNamesProps = {
  names: string[];
  isBold?: boolean;
  // lets callers reuse the exact title element/styles used for the non-group fallback
  renderWrapper?: (children: ReactNode) => ReactNode;
};

export const TruncatedGroupNames = ({ names, isBold, renderWrapper }: TruncatedGroupNamesProps) => {
  const { t } = useTranslation('thread-header');
  const { visibleNames, hiddenNames, shouldShowHiddenNames } = truncatedNames(names);

  const {
    Tooltip,
    triggerProps: hiddenNamesTriggerProps,
    tooltipProps: hiddenNamesTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'top-start',
    offset: 4,
  });

  const namesContent = (
    <>
      {visibleNames.join(', ')}
      {shouldShowHiddenNames && (
        <>
          {', '}
          <span
            tabIndex={0}
            aria-label={t('Show hidden participant names')}
            title={hiddenNames.join(', ')}
            className={hiddenNamesTriggerStyles}
            {...hiddenNamesTriggerProps}
          >
            {t('{{count}} Other', { count: hiddenNames.length })}
          </span>
        </>
      )}
    </>
  );

  return (
    <>
      {renderWrapper ? (
        renderWrapper(namesContent)
      ) : (
        <Text size='medium' weight={isBold ? 'bold' : 'regular'} css={dsStyles.truncate}>
          {namesContent}
        </Text>
      )}
      {shouldShowHiddenNames && (
        // notification toasts use theme.zIndex.alerts, which sits above the tooltip's default z-index, so bump it here
        <Tooltip
          css={css`
            z-index: ${theme.zIndex.alerts + 1};
          `}
          {...hiddenNamesTooltipProps}
        >
          <div className={tooltipContainerStyles}>
            {hiddenNames.map((hiddenName, index) => (
              <span key={`${hiddenName}-${index}`}>{hiddenName}</span>
            ))}
          </div>
        </Tooltip>
      )}
    </>
  );
};
